import React, { ComponentProps, useState } from 'react';

import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { CategoriesAddOrEditForm } from '../partials/CategoriesAddOrEditForm';
import { Icons } from '../../../components/atoms/SVG';
import { CategoriesList } from '../partials/CategoriesList';
import { ContextMenu, ContextMenuButton } from '../../../_lib/components/ContextMenu';
import { CategoriesDeleteForm } from '../partials/CategoriesDeleteForm';
import { CategoryElement } from '../partials/CategoryElement';
import { CategoryEdit } from '../categoriesTypes';
import { CategoriesListItem } from '../partials/CategoriesListItem';
import { useFocusOnRefreshWithTabIndex, useRefreshBlock } from '../../../_lib/BlockFetcher';
import { BlockBar, BlockBarButton } from '../../../_lib/components/BlockBar';
import { CategoriesCreateForm, CategoriesEditForm } from '../partials/CategoryFormTypes';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { asFetchableView } from '../../../_lib/ViewFetcher';

export type CategoriesEditViewProps = {
  categories: CategoryEdit[];
  create: CategoriesCreateForm;
};

// used to differentiate which button was clicked, and therefore which modal to open
export type ActiveFormType = ActiveForm['type'];

type DeleteForm = {
  type: 'delete';
  form: CategoriesEditForm;
  title: string;
};
type EditForm = {
  type: 'edit';
  form: CategoriesEditForm;
};
type AddForm = {
  type: 'add';
  form: CategoriesCreateForm;
};

type ActiveForm = DeleteForm | EditForm | AddForm;

/**
 * EditView for the [x]CategoriesBlock
 * renders only categories with contextmenu.
 */
export function CategoriesEdit(props: CategoriesEditViewProps) {
  const { categories, create } = props;

  const { t } = useSonTranslation('Categories');
  const { refresh, refreshCount } = useRefreshBlock();
  const [currentModal, setCurrentModal] = useState<ActiveForm>();
  const closeModal = () => setCurrentModal(undefined);

  // refreshcount is passed from blockfetch, increasing when fetch has been triggered.
  const focusRef = useFocusOnRefreshWithTabIndex<HTMLDivElement>(refreshCount);

  return (
    <div className="categories-block" ref={focusRef}>
      <BlockBar heading={t('category.add')} hierarchy={3} modifier="border-bottom">
        <BlockBarButton
          onClick={() => setCurrentModal({ type: 'add', form: create })}
          text={t('category.add')}
          svgSpriteId={Icons.ADD_PLUS}
        />
      </BlockBar>
      <CategoriesList categories={categories}>
        {(category) => {
          const { title, image, maxValidityPeriodDays, hexColor, id, forms } = category;

          return (
            <CategoriesListItem key={id} style={{ borderLeftColor: hexColor }}>
              <CategoryElement key={title} title={title} maxValidityPeriodDays={maxValidityPeriodDays} image={image}>
                <ContextMenu tooltipText={t('moreOptions', { title })}>
                  <ContextMenuButton aria-haspopup onClick={() => setCurrentModal({ type: 'edit', form: forms.edit })}>
                    {t('category.edit')}
                  </ContextMenuButton>
                  <ContextMenuButton
                    aria-haspopup
                    onClick={() => setCurrentModal({ type: 'delete', form: forms.delete, title })}
                  >
                    {t('category.delete')}
                  </ContextMenuButton>
                </ContextMenu>
              </CategoryElement>
            </CategoriesListItem>
          );
        }}
      </CategoriesList>

      {currentModal && (
        <ModalDialog initialFocusIndex={1} closeModal={closeModal} headline={t(`category.${currentModal.type}`)}>
          {currentModal.type === 'delete'
            ? (
                <CategoriesDeleteForm
                  closeModal={closeModal}
                  form={currentModal.form}
                  refresh={refresh}
                  title={currentModal.title}
                />
              )
            : (
                <CategoriesAddOrEditForm {...currentModal} refresh={refresh} closeModal={closeModal} />
              )}
        </ModalDialog>
      )}
    </div>
  );
}

export const CategoriesEditView = asFetchableView(CategoriesEdit);
export type CategoriesEditView = ComponentProps<typeof CategoriesEditView>;
